import React, { useState, useEffect, useRef } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../styles/AdjustmentManagement.css';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  LinearProgress,
  Divider,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import api from '../services/api';
import SearchableDataTable from '../components/SearchableDataTable';
import { usePointInTimeFilter, filterDataByPointInTime } from '../hooks/usePointInTimeFilter';
import { useAdjustmentType } from '../components/layout/Layout';

const AdjustmentManagement = () => {
  // State for adjustments
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pointInTimeOptions, setPointInTimeOptions] = useState([]);
  
  // Get PointInTime filter from context
  const { selectedPointInTime } = usePointInTimeFilter();
  
  // Get adjustment type filter from context
  const { selectedAdjustmentType, adjustmentTypes } = useAdjustmentType();
  
  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAdjustment, setCurrentAdjustment] = useState({
    AdjustmentName: '',
    AdjustmentCode: '',
    AdjustmentType: '',
    PointInTime: '',
    ASURollup: '',
    FootnoteRollup: '',
    CustomRollup: '',
    ModifiedBy: 'admin',
    ScenarioCode: 'SC001' // Default scenario code as per backend model
  });
  
  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // State for row selection
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Reference for file input
  const fileInputRef = useRef(null);

  // Adjustment types now come from the AdjustmentTypeContext in Layout.js

  // Fetch adjustments from API
  const fetchAdjustments = async () => {
    setLoading(true);
    try {
      const [adjustmentsResponse, pointInTimesResponse] = await Promise.all([
        api.get('/adjustments/master'),
        api.get('/point-in-times')
      ]);
      
      // Create lookup map for PointInTime codes to names
      const pointInTimeMap = new Map();
      (pointInTimesResponse.data.data || []).forEach(pit => {
        pointInTimeMap.set(pit.Code, pit.Name);
      });
      
      // Transform API data to match frontend schema
      const transformedAdjustments = (adjustmentsResponse.data.data || []).map(adjustment => ({
        id: adjustment.id,
        AdjustmentId: adjustment.AdjustmentId,
        AdjustmentName: adjustment.AdjustmentName,
        AdjustmentCode: adjustment.AdjustmentCode || '',
        AdjustmentType: adjustment.AdjustmentType || '',
        PointInTime: pointInTimeMap.get(adjustment.PointInTime) || adjustment.PointInTime || '',
        PointInTimeCode: adjustment.PointInTime || '', // Keep the original code for editing
        Year: adjustment.Year,
        Period: adjustment.Period,
        ModifiedBy: adjustment.ModifiedBy,
        ModifiedOn: new Date(adjustment.ModifiedOn).toLocaleString()
      }));
      
      setAdjustments(transformedAdjustments);
    } catch (error) {
      console.error('Error fetching adjustments:', error);
      setNotification({
        open: true,
        message: 'Failed to load adjustments',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch PointInTime options
  const fetchPointInTimeOptions = async () => {
    try {
      const response = await api.get('/point-in-times');
      setPointInTimeOptions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching PointInTime options:', error);
      // Set empty array on error - no fallback values
      setPointInTimeOptions([]);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchAdjustments();
    fetchPointInTimeOptions();
  }, []);

  // Handle adding a new adjustment
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentAdjustment({
      AdjustmentName: '',
      AdjustmentCode: '',
      AdjustmentType: 'Standard',
      PointInTime: '',
      ModifiedBy: 'admin'
    });
    setOpenDialog(true);
  };

  // Handle editing an adjustment
  const handleEdit = (adjustment) => {
    setEditMode(true);
    setCurrentAdjustment({
      id: adjustment.id,
      AdjustmentId: adjustment.AdjustmentId,
      AdjustmentName: adjustment.AdjustmentName,
      AdjustmentCode: adjustment.AdjustmentCode || '',
      AdjustmentType: adjustment.AdjustmentType || 'Standard',
      PointInTime: adjustment.PointInTimeCode || '', // Use the code for editing
      ModifiedBy: adjustment.ModifiedBy
    });
    setOpenDialog(true);
  };

  // Handle selection change
  const handleSelectionChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  // Handle deleting selected adjustments
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected adjustments?`)) {
      setDeleting(true);
      try {
        // Delete multiple adjustments
        const deletePromises = selectedRows.map(id => api.delete(`/adjustments/master/${id}`));
        
        Promise.all(deletePromises)
          .then(() => {
            fetchAdjustments(); // Refresh the list
            setSelectedRows([]);
            setNotification({
              open: true,
              message: `Successfully deleted ${selectedRows.length} adjustments`,
              severity: 'success'
            });
          })
          .catch(error => {
            console.error('Error deleting adjustments:', error);
            setNotification({
              open: true,
              message: 'Failed to delete adjustments',
              severity: 'error'
            });
          });
      } catch (error) {
        console.error('Error deleting adjustments:', error);
        setNotification({
          open: true,
          message: 'Failed to delete adjustments',
          severity: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };
  
  // Handle deleting a single adjustment
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this adjustment?')) {
      try {
        await api.delete(`/adjustments/master/${id}`);
        fetchAdjustments(); // Refresh the list
        setNotification({
          open: true,
          message: 'Adjustment deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting adjustment:', error);
        setNotification({
          open: true,
          message: 'Failed to delete adjustment',
          severity: 'error'
        });
      }
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing the dialog
  const handleClose = () => {
    setOpenDialog(false);
    setConfirmCloseOpen(false);
  };

  // Handle saving an adjustment
  const handleSave = async () => {
    try {
      const adjustmentData = {
        AdjustmentName: currentAdjustment.AdjustmentName,
        AdjustmentCode: currentAdjustment.AdjustmentCode,
        AdjustmentType: currentAdjustment.AdjustmentType || 'Standard',
        PointInTime: currentAdjustment.PointInTime,
        ModifiedBy: currentAdjustment.ModifiedBy || 'admin',
        ScenarioCode: currentAdjustment.ScenarioCode || 'SC001' // Required field from backend model
      };

      if (editMode) {
        // Update existing adjustment
        await api.put(`/adjustments/master/${currentAdjustment.id}`, adjustmentData);
        
        // Refresh the adjustments list
        await fetchAdjustments();
        
        setNotification({
          open: true,
          message: 'Adjustment updated successfully',
          severity: 'success'
        });
      } else {
        // Create new adjustment
        await api.post('/adjustments/master', adjustmentData);
        
        // Refresh the adjustments list
        await fetchAdjustments();
        
        setNotification({
          open: true,
          message: 'Adjustment created successfully',
          severity: 'success'
        });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving adjustment:', error);
      const message = error.response?.data?.message || 'Failed to save adjustment';
      setNotification({
        open: true,
        message,
        severity: 'error'
      });
    }
  };

  // Function to export adjustments data to Excel
  const exportToExcel = () => {
    try {
      // Determine which adjustments to export based on selection and current filters
      const adjustmentsToExport = selectedRows.length > 0 
        ? filteredAdjustments.filter(adjustment => selectedRows.includes(adjustment.id))
        : filteredAdjustments;

      // Filter out the actions column and map to export format
      const exportColumns = columns
        .filter(col => col.field !== 'actions' && col.headerName !== 'ACTIONS')
        .map(col => ({
          field: col.field,
          headerName: col.headerName || col.field
        }));

      // Create export data with only the columns we want to export
      const exportData = adjustmentsToExport.map(adjustment => {
        const row = {};
        exportColumns.forEach(col => {
          // Export PointInTime CODE instead of NAME
          if (col.field === 'PointInTime') {
            row[col.headerName] = adjustment.PointInTimeCode || adjustment[col.field];
          } else {
            row[col.headerName] = adjustment[col.field];
          }
        });
        return row;
      });
      
      // Create a worksheet from the exported data
      // Always include headers even if no data by specifying header option
      const worksheet = XLSX.utils.json_to_sheet(exportData, {
        header: exportColumns.map(col => col.headerName)
      });
      
      // Auto-size columns
      const columnWidths = exportColumns.map(col => ({
        wch: Math.max(
          col.headerName ? col.headerName.length : 0,
          ...exportData.map(row => String(row[col.headerName] || '').length)
        ) + 2 // Add some padding
      }));
      
      worksheet['!cols'] = columnWidths;
      
      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Adjustments');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array',
        bookSST: true 
      });
      
      const data = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Save the file with current date
      const today = new Date().toISOString().split('T')[0];
      saveAs(data, `Adjustments_Export_${today}.xlsx`);
      
      setNotification({
        open: true,
        message: `Successfully exported ${adjustmentsToExport.length} adjustment${adjustmentsToExport.length !== 1 ? 's' : ''}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting adjustments:', error);
      setNotification({
        open: true,
        message: 'Failed to export adjustments: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  // Handle import click
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Handle file selection for import
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first worksheet
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          // Check if file is empty
          if (jsonData.length === 0) {
            throw new Error('The Excel file is empty');
          }
          
          // Fetch PointInTime master data to map names/codes to codes
          const pointInTimesResponse = await api.get('/point-in-times');
          const pointInTimes = pointInTimesResponse.data.data || [];
          
          // Create a map that accepts both PointInTime Names and Codes (case-insensitive)
          // Export now uses codes, but we support names for backward compatibility
          const pitNameToCodeMap = new Map();
          pointInTimes.forEach(pit => {
            pitNameToCodeMap.set(pit.Name.toLowerCase().trim(), pit.Code);
            // Map the code to itself so codes work directly (primary format)
            pitNameToCodeMap.set(pit.Code.toLowerCase().trim(), pit.Code);
          });
          
          // Get all column names from the first row, case-insensitive
          const fileColumns = Object.keys(jsonData[0] || {});
          const fileColumnsLower = fileColumns.map(col => col.toLowerCase());
          
          // Map frontend column names to possible Excel column names
          const columnMappings = {
            'AdjustmentCode': ['adjustment code', 'adjustmentcode', 'code'],
            'AdjustmentName': ['name', 'adjustment name', 'adjustmentname'],
            'AdjustmentType': ['type', 'adjustment type', 'adjustmenttype'],
            'PointInTime': ['point in time', 'pointintime', 'pit', 'period']
          };
          
          // Find matching columns in the Excel file
          const matchedColumns = {};
          let missingColumns = [];
          
          for (const [field, possibleNames] of Object.entries(columnMappings)) {
            const found = fileColumnsLower.some((col, index) => {
              if (possibleNames.includes(col)) {
                matchedColumns[field] = fileColumns[index]; // Store original column name
                return true;
              }
              return false;
            });
            
            if (!found && ['AdjustmentCode', 'AdjustmentName', 'AdjustmentType', 'PointInTime'].includes(field)) {
              missingColumns.push(possibleNames[0]); // Use the first name as the display name
            }
          }
          
          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }
          
          // First, fetch all existing adjustments to check for duplicates
          const existingAdjustments = await api.get('/adjustments/master');
          // Map by both AdjustmentCode and PointInTime for composite key matching
          const adjustmentsMap = new Map(
            existingAdjustments.data.data.map(adj => [
              `${adj.AdjustmentCode.toLowerCase()}_${adj.PointInTime.toLowerCase()}`, 
              adj
            ])
          );
          
          // Process each row and prepare for import/update
          const importResults = {
            created: 0,
            updated: 0,
            errors: []
          };
          
          const processRow = async (row, index) => {
            try {
              // Extract values using matched column names
              const adjustmentCode = String(row[matchedColumns.AdjustmentCode] || '').trim();
              const adjustmentName = String(row[matchedColumns.AdjustmentName] || '').trim();
              const adjustmentType = String(row[matchedColumns.AdjustmentType] || '').trim();
              const pointInTimeInput = String(row[matchedColumns.PointInTime] || '').trim();
              
              // Validate required fields
              if (!adjustmentCode) {
                throw new Error('Adjustment Code is required');
              }
              if (!adjustmentName) {
                throw new Error('Adjustment Name is required');
              }
              if (!adjustmentType) {
                throw new Error('Adjustment Type is required');
              }
              if (!pointInTimeInput) {
                throw new Error('Point In Time is required');
              }
              
              // Validate adjustment type against predefined types (dropdown values)
              const validAdjustmentTypes = adjustmentTypes.filter(type => type !== 'All');
              if (!validAdjustmentTypes.includes(adjustmentType)) {
                throw new Error(`Invalid Adjustment Type: "${adjustmentType}". Must be one of: ${validAdjustmentTypes.join(', ')}`);
              }

              // Map PointInTime Name to Code (case-insensitive lookup)
              const pointInTimeCode = pitNameToCodeMap.get(pointInTimeInput.toLowerCase().trim());
              
              if (!pointInTimeCode) {
                throw new Error(`Invalid Point In Time: "${pointInTimeInput}". Must match a valid PointInTime Name or Code.`);
              }

              // Build payload with PointInTime CODE
              const adjustmentData = {
                AdjustmentName: adjustmentName,
                AdjustmentCode: adjustmentCode,
                AdjustmentType: adjustmentType,
                PointInTime: pointInTimeCode, // Store the CODE, not the name
                ModifiedBy: 'admin',
                ScenarioCode: 'SC001' // Default scenario code as per backend model
              };

              // Check if adjustment with this code AND PointInTime already exists
              const compositeKey = `${adjustmentCode.toLowerCase()}_${pointInTimeCode.toLowerCase()}`;
              const existingAdjustment = adjustmentsMap.get(compositeKey);

              if (existingAdjustment) {
                // Update existing adjustment (same AdjustmentCode and PointInTime)
                await api.put(`/adjustments/master/${existingAdjustment.id}`, adjustmentData);
                importResults.updated++;
              } else {
                // Create new adjustment (different AdjustmentCode or PointInTime combination)
                await api.post('/adjustments/master', adjustmentData);
                importResults.created++;
              }
              
              return { success: true };
              
            } catch (error) {
              console.error(`Error processing row ${index + 1}:`, row, error);
              const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
              importResults.errors.push(`Row ${index + 1}: ${errorMessage}`);
              return { success: false, error: errorMessage };
            }
          };
          
          // Process rows sequentially to avoid race conditions
          for (let i = 0; i < jsonData.length; i++) {
            await processRow(jsonData[i], i);
          }
          
          // Check if there were any errors
          if (importResults.errors.length > 0) {
            const errorMessage = importResults.errors.length === jsonData.length 
              ? 'All rows had errors. No data was imported.'
              : `Completed with ${importResults.errors.length} error(s) out of ${jsonData.length} rows.`;
              
            if (importResults.created === 0 && importResults.updated === 0) {
              throw new Error(`Import failed: ${errorMessage}\n\n${importResults.errors.join('\n')}`);
            } else {
              // Some rows succeeded, show warning
              setNotification({
                open: true,
                message: `Partial import: ${importResults.created} created, ${importResults.updated} updated, ${importResults.errors.length} errors`,
                severity: 'warning',
                autoHideDuration: 10000
              });
            }
          }
          
          // Prepare success response
          const response = {
            data: {
              created: importResults.created,
              updated: importResults.updated,
              errors: importResults.errors,
              total: jsonData.length
            }
          };
          
          // Refresh data
          await fetchAdjustments();
          
          // Only show success message if no errors were shown before
          if (importResults.errors.length === 0) {
            const updatedMsg = importResults.updated > 0 ? `, updated ${importResults.updated}` : '';
            setNotification({
              open: true,
              message: `Successfully imported ${importResults.created} adjustment${importResults.created !== 1 ? 's' : ''}${updatedMsg}`,
              severity: 'success',
              autoHideDuration: 5000
            });
          }
        } catch (error) {
          console.error('Error processing import:', error);
          setNotification({
            open: true,
            message: `Import failed: ${error.message || 'Unknown error'}`,
            severity: 'error'
          });
        }
      };
      
      reader.onerror = () => {
        setNotification({
          open: true,
          message: 'Error reading file',
          severity: 'error'
        });
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error in import handler:', error);
      setNotification({
        open: true,
        message: `Import failed: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setImporting(false);
      // Clear the file input
      event.target.value = null;
    }
  };

  // Handle notification close
  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  // Define columns for the data grid
  const columns = [
    {
      field: 'AdjustmentCode',
      headerName: 'ADJUSTMENT CODE',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <span style={{ fontWeight: 'bold' }}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'AdjustmentName',
      headerName: 'NAME',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'AdjustmentType',
      headerName: 'TYPE',
      flex: 1,
      minWidth: 200,
    },
    
    {
      field: 'PointInTime',
      headerName: 'POINT IN TIME',
      width: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} title="Edit Adjustment">
            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row.id)} title="Delete Adjustment">
            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Filter adjustments by PointInTime and adjustment type
  const filteredAdjustments = React.useMemo(() => {
    // First filter by PointInTime
    let filtered = adjustments;
    
    if (selectedPointInTime && selectedPointInTime !== 'All') {
      filtered = filtered.filter(adj => adj.PointInTimeCode === selectedPointInTime);
    }
    
    // Then filter by adjustment type if not 'All'
    if (selectedAdjustmentType && selectedAdjustmentType !== 'All') {
      filtered = filtered.filter(adj => {
        return adj.AdjustmentType === selectedAdjustmentType || 
               adj.AdjustmentName.includes(selectedAdjustmentType);
      });
    }
    
    return filtered;
  }, [adjustments, selectedPointInTime, selectedAdjustmentType]);

  return (
    <Box className="adjustment-management-container">
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Box className="main-content">
        {/* Toolbar */}
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>Manage Adjustments</Typography>
            {selectedRows.length > 0 && (
              <Typography variant="body2" className="selection-info" sx={{ ml: 2 }}>
                {selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
            )}
          </Box>
          <Box className="button-group">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              className="action-button"
              onClick={exportToExcel}
              disabled={adjustments.length === 0}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              className="action-button"
              onClick={handleImportClick}
              disabled={importing}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
            {/* Hidden file input for Excel import */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".xlsx, .xls"
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              className="action-button"
            >
              Add
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0 || deleting}
              className="action-button"
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4,
                }
              }}
            >
              {deleting ? 'Deleting...' : 'Remove'}
            </Button>
          </Box>
        </Box>
        
        {/* Data Table */}
        <Box className="table-container">
          {loading ? (
            <LinearProgress />
          ) : (
            <SearchableDataTable
              searchPlaceholder="Search Adjustments..."
              rows={filteredAdjustments}
              columns={columns}
              loading={loading}
              pageSize={15}
              tableName="adjustments-table"
              defaultSortField="AdjustmentName"
              defaultSortOrder="asc"
              onRefresh={fetchAdjustments}
              checkboxSelection={true}
              selectionModel={selectedRows}
              onSelectionModelChange={handleSelectionChange}
              height="100%"
              className="custom-datagrid"
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)}
      />
      <Dialog
        open={openDialog}
        onClose={handleRequestClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editMode ? 'Edit Adjustment' : 'Add New Adjustment'}</DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Adjustment Name"
                value={currentAdjustment.AdjustmentName || ''}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, AdjustmentName: e.target.value })}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="adjustment-type-label" required>Adjustment Type</InputLabel>
                <Select
                  labelId="adjustment-type-label"
                  value={currentAdjustment.AdjustmentType || ''}
                  label="Adjustment Type"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, AdjustmentType: e.target.value })}
                  required
                >
                  {/* Skip the 'All' option and hidden types for individual adjustments */}
                  {adjustmentTypes && adjustmentTypes.filter(type => type !== 'All' && type !== 'State Tax Effected Perm').map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Adjustment Code"
                value={currentAdjustment.AdjustmentCode || ''}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, AdjustmentCode: e.target.value })}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="point-in-time-label" required>Point In Time</InputLabel>
                <Select
                  labelId="point-in-time-label"
                  value={currentAdjustment.PointInTime || ''}
                  label="Point In Time"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, PointInTime: e.target.value })}
                  required
                >
                  {pointInTimeOptions.map((pit) => (
                    <MenuItem key={pit.id} value={pit.Code}>
                      {pit.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ASU Rollup"
                value={currentAdjustment.ASURollup || ''}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, ASURollup: e.target.value })}
                autoComplete="off"
              />
            </Grid>
            {/* Year and Period removed per backend change allowing nulls */}

            <Grid item xs={12}>
              <Divider style={{marginTop: '1rem', marginBottom: '1rem'}} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Footnote Rollup"
                value={currentAdjustment.FootnoteRollup || ''}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, FootnoteRollup: e.target.value })}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Rollup"
                value={currentAdjustment.CustomRollup || ''}
                onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, CustomRollup: e.target.value })}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={!currentAdjustment.AdjustmentName || !currentAdjustment.AdjustmentCode || !currentAdjustment.AdjustmentType || !currentAdjustment.PointInTime}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdjustmentManagement;
